import React, { useMemo } from 'react';
import { Checkbox, Collapse, Space, Table, Tag, Typography } from 'antd';
import { PermissionActionColumn } from './permission-action-column';
import type { ParsedPermission } from 'services/permissions';

const RESOURCE_ACTIONS_MAP: Record<string, string> = {
  list: 'Listagem',
  show: 'Visualizar',
  create: 'Criar',
  edit: 'Editar',
  update: 'Atualizar (deprecated)',
  delete: 'Deletar',
  destroy: 'Deletar (deprecated)',
  slug: 'Slug',
  cancel: 'Cancelar',
  approve: 'Aprovar',
  deal: 'Negociar',
  interrupt: 'Interromper',
  close: 'Finalizar',
  magic_link: 'Magic Link'
};

interface PermissionGroupProps {
  permission: ParsedPermission;
  selectedPermission: string[];
  onToggleAll: (name: string, checked: boolean) => void;
  onTogglePermission: (permission: string, parent: string) => void;
  disabled?: boolean;
  [key: string]: unknown;
}

export const PermissionsGroup = React.memo(
  ({
    permission,
    selectedPermission,
    onToggleAll,
    onTogglePermission,
    disabled,
    ...panelProps
  }: PermissionGroupProps) => {
    const availablePermissions = useMemo(
      () =>
        permission.children?.flatMap(child =>
          (child.actions || []).map(action => `${child.name}:${action}`)
        ) || [],
      [permission.children]
    );

    const selectedInArea = useMemo(
      () =>
        availablePermissions.filter(currentPermission =>
          selectedPermission.includes(currentPermission)
        ).length,
      [availablePermissions, selectedPermission]
    );

    const allChecked = useMemo(
      () =>
        (availablePermissions.length > 0 &&
          selectedInArea === availablePermissions.length) ||
        selectedPermission.includes(`${permission.name}:*`),
      [
        availablePermissions.length,
        permission.name,
        selectedInArea,
        selectedPermission
      ]
    );

    return (
      <Collapse.Panel
        {...(panelProps as unknown as React.ComponentProps<
          typeof Collapse.Panel
        >)}
        key={permission.name}
        header={
          <Space size="middle">
            <Typography.Text strong>{permission.title}</Typography.Text>
            <Tag>{`${selectedInArea}/${availablePermissions.length}`}</Tag>
          </Space>
        }
        extra={
          <Checkbox
            disabled={disabled}
            checked={allChecked}
            onClick={event => event.stopPropagation()}
            onChange={event =>
              onToggleAll(permission.name, event.target.checked)
            }
          >
            Selecionar tudo
          </Checkbox>
        }
      >
        <Table dataSource={permission.children} pagination={false} size="small">
          <Table.Column key="title" dataIndex="title" title="" />

          {permission.groupActions?.map(action => (
            <Table.Column
              key={action}
              dataIndex={action}
              align="center"
              width={80}
              title={RESOURCE_ACTIONS_MAP[action]}
              render={(_, record: ParsedPermission) => (
                <PermissionActionColumn
                  value={`${record.name}:${action}`}
                  record={record}
                  action={action}
                  onCheck={onTogglePermission}
                  disabled={disabled}
                  checked={selectedPermission.includes(
                    `${record.name}:${action}`
                  )}
                />
              )}
            />
          ))}
        </Table>
      </Collapse.Panel>
    );
  }
);

PermissionsGroup.displayName = 'PermissionsGroup';
